import React, { useState, useEffect, useRef } from "react";
import styles from "./AccessSubPages.module.css";
import { AccessSubPageShell, useScrollToTop } from "./AccessSubPageShell";
import MyraChatService from "../../../voice-agent/MyraChatService";
import { buildLandingScreenPayload, persistLandingMessage } from "../../utils/myraHelpers";
import { AnalyticsService } from "../../analytics/AnalyticsService";

/* ============================================================================
   AccessHowItWorks.jsx
   Medicare ACCESS Google Ads subpage — "How It Works"

   Content is the how-this-works section (S5) of the ACCESS landing page,
   lbpNewLandingPage.jsx, plus the Myra chat widget and the 1-800-MEDICARE
   block from S8. Per Arjun, the chat widget appears on this subpage only.

   One copy change from the LBP landing page: step 2 uses the general
   problem-area wording ("your problem area"), per the general ACCESS copy
   spec, Section 3.4.

   Chrome, CTA band and footer come from AccessSubPageShell.

   DEV NOTE — the import path for MyraChatService above assumes these files
   sit alongside lbpNewLandingPage.jsx. Adjust if they land elsewhere.
   ============================================================================ */

/* ----------------------------------------------------------------------------
   MYRA SCREEN NAME

   DEV NOTE — this is the landing page's value ("intro") carried over
   unchanged, so subpage Myra traffic is currently indistinguishable from
   landing page Myra traffic in reporting. Whether the two must be separable
   is an open item (Google Ads subpages handoff, Section 5, item 7). If a
   distinct value is wanted, change it here only.
   ---------------------------------------------------------------------------- */
const MYRA_SCREEN_NAME = "intro";

const CHAT_CHIPS = ["How does this work?", "What is the ACCESS Model?", "Why do you need my Medicare number?", "Is this really free?", "Can I start now?"];

export const AccessHowItWorks = () => {
  useScrollToTop();

  const [chatMessages, setChatMessages] = useState([{ who: "m", text: "Hello. I'm Myra. Ask me anything about this program and I'll explain it as plainly as I can." }]);
  const [chatInput, setChatInput] = useState("");
  const [landingTyping, setLandingTyping] = useState(false);

  const chatLogRef = useRef(null);
  const landingTurnRef = useRef(0);
  const landingApiHistoryRef = useRef([]);

  /* Restores any landing-chat history from this browser session, matching
     lbpNewLandingPage.jsx so a visitor who chatted on one surface is not
     starting over on the other. */
  useEffect(() => {
    if (!window.landing_chat) {
      try {
        const saved = localStorage.getItem("landing_chat");
        window.landing_chat = saved ? JSON.parse(saved) : [];
      } catch (e) {
        window.landing_chat = [];
      }
    }
    landingApiHistoryRef.current = window.landing_chat;
  }, []);

  useEffect(() => {
    if (chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [chatMessages, landingTyping]);

  /* DEV NOTE — analytics is not wired here on purpose. See the note in
     AccessAboutProgram.jsx; the same three open items apply. */
  useEffect(() => {
    // Track page view for the How It Works subpage
    const trackingFields = {
      variant: new URLSearchParams(window.location.search).get("variant") || localStorage.getItem("variant") || null,
      isComeFromGoogle: !!(new URLSearchParams(window.location.search).get("gclid") || localStorage.getItem("variant")),
      visitedAccessHowItWorks: true,
      access_subpage: "how_it_works",
    };

    AnalyticsService.startSession(trackingFields);
    AnalyticsService.trackStep({
      ...trackingFields,
      session_last_update: new Date().toISOString(),
    });
  }, []);

  const addChatMessage = (who, text) => {
    setChatMessages((prev) => [...prev, { who, text }]);
  };

  const sendToMyra = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;

    addChatMessage("u", trimmed);
    persistLandingMessage("user", trimmed);
    landingApiHistoryRef.current = [...landingApiHistoryRef.current, { role: "user", content: trimmed }];

    const screenPayload = buildLandingScreenPayload(MYRA_SCREEN_NAME);
    const sessionId = window.trSessionId || "";
    landingTurnRef.current += 1;

    setLandingTyping(true);
    try {
      const result = await MyraChatService.sendMessage(trimmed, sessionId, landingApiHistoryRef.current, landingTurnRef.current, screenPayload, MYRA_SCREEN_NAME);

      addChatMessage("m", result.response);
      persistLandingMessage("assistant", result.response);
      landingApiHistoryRef.current = result.history && result.history.length ? result.history : [...landingApiHistoryRef.current, { role: "assistant", content: result.response }];
    } finally {
      setLandingTyping(false);
    }
  };

  const sendChat = () => {
    const v = chatInput;
    if (!v.trim()) return;
    setChatInput("");
    sendToMyra(v);
  };

  const askMyra = (q) => {
    sendToMyra(q);
  };

  return (
    <AccessSubPageShell activePage="how">
      {/* Typing-indicator animation — scoped here since it isn't in the CSS
          module, matching lbpNewLandingPage.jsx. */}
      <style>{`
        @keyframes myraTypingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        .myraTypingDot {
          display: inline-block;
          width: 6px;
          height: 6px;
          margin: 0 2px;
          border-radius: 50%;
          background: currentColor;
          animation: myraTypingBounce 1.2s infinite ease-in-out;
        }
        .myraTypingDot:nth-child(2) { animation-delay: 0.15s; }
        .myraTypingDot:nth-child(3) { animation-delay: 0.3s; }
      `}</style>

      {/* ===== HERO ===== */}
      <div className={styles.subhero}>
        <div className={styles.container}>
          <span className={styles.label}>What to expect</span>
          <h1>How this works.</h1>
          <p>Medicare is testing a new way to pay for physical therapy you do at home. If you qualify, here is what taking part looks like from start to finish.</p>
          <span className={styles.duration}>The program runs for 12 months</span>
        </div>
      </div>

      {/* ===== BODY — landing page S5 ===== */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.steps5}>
            <div className={styles.s5}>
              <div className={styles.s5N}>1</div>
              <div>
                <h4>Tell us where you live, then check if you qualify</h4>
                <p>First, we will ask which state you live in so we can confirm we have licensed physical therapists serving your area. Then you will answer a few short questions, and we check your eligibility with Medicare in real time. Most answers come back in under a minute — you will see a progress indicator while we wait.</p>
              </div>
            </div>
            <div className={styles.s5}>
              <div className={styles.s5N}>2</div>
              <div>
                <h4>Once Medicare confirms you qualify, meet Myra</h4>
                <p>Myra will ask about your problem area and guide you through one gentle movement. The assessment takes about 15 minutes, and you will see exactly which stretches to start with right away.</p>
              </div>
            </div>
            <div className={styles.s5}>
              <div className={styles.s5N}>3</div>
              <div>
                <h4>Short sessions at home</h4>
                <p>Myra leads you through your stretches and exercises. Sessions take 10 to 20 minutes.</p>
              </div>
            </div>
            <div className={styles.s5}>
              <div className={styles.s5N}>4</div>
              <div>
                <h4>Questions every 90 days</h4>
                <p>Roughly every 90 days, Myra asks you a short set of questions about your pain and what you can do day to day. Virdio Health reports those answers to Medicare. That is how Medicare sees whether the program is working.</p>
              </div>
            </div>
            <div className={styles.s5}>
              <div className={styles.s5N}>5</div>
              <div>
                <h4>Talk to Myra anytime</h4>
                <p>If a session ever feels too easy or too hard, just tell Myra and she will adjust right away. You do not have to wait. And because Medicare holds back part of what it pays us until it sees whether the people we treat actually improve, your results genuinely matter to us.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== S8 MYRA CHAT — this subpage only ===== */}
      <section className={styles.qsection}>
        <div className={styles.container}>
          <span className={styles.label}>If you get stuck</span>
          <h2>Questions along the way? Just ask.</h2>
          <p>Myra is with you the whole way, on this page and every step after it. Try her now — ask anything about the program and she will explain it as plainly as she can.</p>

          <div className={styles.chat}>
            <div className={styles.chatHead}>
              <div className={styles.chatDot}>M</div>
              <div>
                <b>Myra</b> <span>· AI recovery coach</span>
              </div>
            </div>
            <div className={styles.chatLog} ref={chatLogRef}>
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`${styles.msg} ${msg.who === "u" ? styles.u : styles.m}`}>
                  {msg.text}
                </div>
              ))}
              {landingTyping && (
                <div className={`${styles.msg} ${styles.m}`} aria-live="polite" aria-label="Myra is typing">
                  <span className="myraTypingDot" />
                  <span className="myraTypingDot" />
                  <span className="myraTypingDot" />
                </div>
              )}
            </div>
            <div className={styles.chips}>
              {CHAT_CHIPS.map((q) => (
                <button key={q} className={styles.chip} onClick={() => askMyra(q)}>
                  {q}
                </button>
              ))}
            </div>
            <div className={styles.chatIn}>
              <input
                type="text"
                placeholder="Type your question here"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendChat();
                }}
              />
              <button onClick={sendChat}>Send</button>
            </div>
          </div>

          <div className={styles.medbox}>
            <h3>Questions about your Medicare records or coverage</h3>
            <p>Medicare is the right place to ask.</p>
            <p>
              <strong>1-800-MEDICARE (1-800-633-4227)</strong>
              <br />
              TTY users can call <strong>1-877-486-2048</strong>
            </p>
            <p>
              You can also read about the ACCESS Model on Medicare's own website at
              <a href="https://www.medicare.gov/access" target="_blank" rel="noopener noreferrer">
                <strong> Medicare.gov/ACCESS</strong>
              </a>
              , and find Virdio Health on the
              <a href="https://www.cms.gov/priorities/innovation/access-model-accepted-applicants" target="_blank" rel="noopener noreferrer">
                <strong> CMS list of accepted ACCESS participants</strong>
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </AccessSubPageShell>
  );
};

export default AccessHowItWorks;
